import React from "react";
import { Platform } from "react-native";
export var InputVariant;
(function (InputVariant) {
    InputVariant["normal"] = "normal";
    InputVariant["search"] = "search";
})(InputVariant || (InputVariant = {}));
export const Input = Platform.OS === 'web' ? require("./input-web").InputWeb : require("./input-native").InputNative;
/**
 * Usage:
 * ```
 * const nameInputControl = useInputControl();
 *
 * const onSubmit = React.useCallback(() => {
 *   // consume nameInputControl.value
 * }, [nameInputControl.value]);
 *
 * return (
 *   <Page>
 *     <Input
 *       placeholder="Taylor Doe"
 *       {...nameInputControl.inputProps}
 *     />
 *   </Page>
 * )
 * ```
 *
 * ## Why?
 * This makes input handling smoother as the input remains uncontrolled but still preserves the stateful behavior for external consumers.
 *
 * ### Mounting
 * Default value with useInputControl is applied when the input is mounted. The
 * default value can be freely changed before mount, but once the input is mounted
 * changing the defaultValue will have no effect
 *
 * Calls to update() only take effect after the input is mounted. If update is
 * called before mount, an error will be thrown.
 */
export function useInputControl(opts) {
    // Ref the input opts so that callbacks are never re-rendered
    const optsRef = React.useRef(opts ?? null);
    optsRef.current = opts ?? null;
    const inputRef = React.useRef(null);
    const valueRef = React.useRef(opts?.defaultValue ?? "");
    const [value, setValue] = React.useState(opts?.defaultValue ?? "");
    const [actualValue, setActualValue] = React.useState(Platform.OS === "web" ? undefined : value);
    const isMounted = React.useRef(false);
    // Side-Effects
    React.useEffect(() => {
        valueRef.current = value;
    }, [value]);
    // Callbacks
    const onChangeValue = React.useCallback((newValue) => {
        if (Platform.OS !== "web") {
            setActualValue(newValue);
        }
        valueRef.current = newValue;
        setValue(newValue);
        optsRef.current?.onChangeValue?.(newValue);
    }, []);
    const update = React.useCallback((newValue) => {
        if (isMounted.current === false) {
            throw new Error("Cannot update input value before input is mounted. To set a value before mount, use the defaultValue property of useInputControl(), this value can be changed freely before mount");
        }
        setValue(newValue);
        if (inputRef.current != null) {
            if (Platform.OS !== "web") {
                setActualValue(newValue);
            }
            else {
                inputRef.current.value = newValue;
            }
        }
        ;
    }, []);
    const focus = React.useCallback(() => {
        if (inputRef.current)
            inputRef.current.focus();
    }, []);
    const onMount = React.useCallback(() => {
        if (inputRef.current)
            inputRef.current.value = optsRef.current?.defaultValue ?? "";
        setValue(optsRef.current?.defaultValue ?? "");
        valueRef.current = optsRef.current?.defaultValue ?? "";
        isMounted.current = true;
    }, []);
    const ref = React.useCallback((el) => {
        inputRef.current = el;
        if (isMounted.current !== true)
            onMount();
    }, []);
    // Return Values
    const inputProps = React.useMemo(() => {
        return {
            ref,
            value: actualValue,
            onChangeValue,
            onChangeText: onChangeValue,
        };
    }, [actualValue]);
    return React.useMemo(() => {
        return {
            value,
            valueRef,
            update,
            focus,
            inputProps
        };
    }, [value]);
}
